import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ChipColor } from '@predictiveindex/tapestry';
import {
    BehaviorSubject,
    Observable,
    Subject,
    debounceTime,
    defer,
    delay,
    distinctUntilChanged,
    filter,
    from,
    fromEvent,
    map,
    of,
    startWith,
    switchMap,
    take,
    takeUntil
} from 'rxjs';
import { SortDirection, SortType } from '../../constants-types';
import {
    FilterInfo,
    FilterType,
    SortInfo,
    UserStub,
    UserStubWithState
} from '../../core-data-model';
import { UserApi } from '../../core-services';
import { UserFactory } from '../../object-factory';
import { InitDataService } from '../../system-services';
import { TranslationService } from '../../translations';
import { EmailDomainValidatorDirective } from '../Directives';

export interface ValidationError {
    validationCode: ValidationCode;
    message: string;
}

export enum ValidationCode {
    UNKNOWN,
    INVALID_EMAIL_FORMAT,
    INVALID_DOMAIN,
    DUPLICATE_ENTRY,
    EMAIL_ALREADY_EXISTS,
    PENDING_INVITATION_KNOWN,
    EXCEEDS_MAXIMUM_INPUT,
    USER_DEFINED_VALIDATION
}

/**
 * id, firstname, lastname, email must follow UserStub properties
 */
export interface EmailEntity {
    id?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    valid: boolean;
}

@Component({
    selector: 'email-chip-input-form',
    templateUrl: './email-chip-input-form.html',
    styleUrls: ['./email-chip-input-form.component.less'],
    standalone: false
})
export class EmailChipInputComponent implements OnInit, OnDestroy {
    /**
     * Array of the organization's users.
     */
    @Input() existingUserEmails$!: Observable<EmailEntity[]>;
    @Input() maximumEntries = 20;
    @Input() showCounter = true;
    @Input() disabled = false;

    /**
     * Emits all emails on Add User when all validate.
     * Emits an empty array if an email contains an error.
     */
    @Output() changed = new EventEmitter<EmailEntity[]>();

    form = this.fb.group({
        emails: [{ value: '', disabled: true }]
    });
    emails: EmailEntity[] = [];
    validationErrors: ValidationError[] = [];
    validDomains: string[] = [];
    existingUserEmails: EmailEntity[] = [];
    emailChipColor = ChipColor.Blue;

    // streams
    protected typeaheadList$: Observable<UserStubWithState[]>;
    private isMouseDown$ = new BehaviorSubject(false);
    private destroyed$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private TR: TranslationService,
        private initDataService: InitDataService,
        private userApi: UserApi,
        private userFactory: UserFactory
    ) {}

    private get emailsControl() {
        return this.form.controls.emails;
    }

    ngOnInit(): void {
        this.existingUserEmails$
            .pipe(
                filter((emails) => emails?.length > 0),
                takeUntil(this.destroyed$)
            )
            .subscribe((emails) => {
                this.existingUserEmails = emails;
                this.validDomains = this.getValidDomains(emails);
                this.emailsControl.enable();
            });

        this.form.controls.emails.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.removeValidationError(
                    ValidationCode.EXCEEDS_MAXIMUM_INPUT
                );
            });

        this.typeaheadList$ = this.form.controls.emails.valueChanges.pipe(
            startWith(''),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((item) => this.autoCompleteFilter(item || ''))
        );

        fromEvent(document.body, 'mousedown')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.isMouseDown$.next(true));

        fromEvent(document.body, 'mouseup')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.isMouseDown$.next(false));
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    /**
     * Adds emails to the array when form is submitted.
     */
    public addEmails(emails: EmailEntity[] = null) {
        if (!emails) {
            emails = this.emailsControl?.value
                ? this.emailsControl.value
                      .split(/[,;]/)
                      .map((e) => e.trim())
                      .filter((e) => !!e)
                      .map((e) => ({ email: e, valid: true }))
                : [];
        }

        const emailCount =
            this.emails.filter((emailEntity) => emailEntity.valid).length +
            emails.length;

        if (emailCount > this.maximumEntries) {
            this.addValidationError(
                ValidationCode.EXCEEDS_MAXIMUM_INPUT,
                this.TR.key(
                    'client-admin.users.invite-users.exceeds-count-message'
                )
            );
            this.changed.emit([]);
        } else {
            this.removeValidationError(ValidationCode.EXCEEDS_MAXIMUM_INPUT);
            this.emails = this.emails.concat(emails);
            this.form.reset();
            this.executeOutput();
        }
    }

    /**
     * Removes the specified email from the array.
     *
     * @param filterEmail email to be removed
     */
    public removeEmail(filterEmail: EmailEntity) {
        this.emails = this.emails.filter(
            (emailEntity) => emailEntity !== filterEmail
        );
        this.executeOutput();
    }

    /**
     * Clears all emails
     */
    public clearEmails() {
        this.emails = [];
        this.executeOutput();
    }

    /**
     * Adds email list using addEmails if autocomplete
     * selection isn't also in progress (via mouse up/down).
     */
    protected handleFocusOut(): void {
        /**
         *  If the mouse is down currently, then wait for mouse up.
         */
        if (this.isMouseDown$.value) {
            this.mouseUp()
                .pipe(delay(100))
                .subscribe(() => {
                    this.addEmails();
                });
        } else {
            this.addEmails();
        }
    }

    /**
     * Adds autocomplete selection to email list using addEmails
     *
     * @param event
     */
    protected handleOptionSelected(event: MatAutocompleteSelectedEvent): void {
        this.addEmails([event.option.value]);
        this.inputReset();
    }

    /**
     * If Autocomplete result has already been added
     *
     * @param user
     * @returns
     */
    protected doesUserAlreadyExist(user: UserStub): boolean {
        return (
            !this.emails.every(
                (x) => x.email.toLowerCase() !== user.email.toLowerCase()
            ) ||
            !this.existingUserEmails.every(
                (x) => x.email.toLowerCase() !== user.email.toLowerCase()
            )
        );
    }

    /**
     * Emits true when the mouse has been released.
     */
    private mouseUp = () =>
        this.isMouseDown$.pipe(
            takeUntil(this.destroyed$),
            map((isMouseDown) => !isMouseDown),
            filter((isMouseReleased) => isMouseReleased),
            take(1)
        );

    /**
     * Executes the output based on validation.
     */
    private executeOutput(): void {
        if (this.validateEntries()) {
            this.changed.emit(this.emails);
        } else {
            this.changed.emit([]);
        }
    }

    /**
     * Returns a distinct list of email domains in a user list.
     * Leaves out the @ sign from domain
     *
     * @param users List of users supplied to the component to parse domains.
     * @returns string array of valid domains.
     */
    private getValidDomains(emails: EmailEntity[]): string[] {
        if (
            !this.initDataService.companyEmailDomains ||
            !this.initDataService.companyEmailDomains.allowList ||
            this.initDataService.companyEmailDomains.allowList.length === 0
        ) {
            return emails
                .map((entity) => {
                    const domain = EmailDomainValidatorDirective.extractDomain(
                        entity.email
                    );
                    if (domain) {
                        return domain.toLowerCase();
                    }
                })
                .filter(
                    (domain, index, array) => array.indexOf(domain) === index
                );
        }
        // companyEmailDomains are expected to include @ sign.
        return this.initDataService.companyEmailDomains.allowList;
    }

    /**
     * Executes all validations.
     *
     * @returns boolean value of all validations.
     */
    private validateEntries(): boolean {
        this.resetValidation();

        const validEmails = this.validateEmailFormat();
        const validDomains = this.validateAllowedEmailDomains();
        const validOrgUser = this.validateDuplicateUser();
        const validDuplicates = this.validateDuplicateEmailEntered();

        return validEmails && validDomains && validOrgUser && validDuplicates;
    }

    /**
     * Validates the array of emails to ensure a valid email address was entered.
     *
     * @param emails email array to validate.
     * @returns boolean value whether emails are valid or invalid.
     */
    private validateEmailFormat(): boolean {
        let valid = true;
        for (const emailEntity of this.emails) {
            const invalidEmailError = Validators.email(<AbstractControl>{
                value: emailEntity.email
            });

            if (invalidEmailError !== null) {
                emailEntity.valid = false;
                valid = false;
            }
        }

        if (valid) {
            this.removeValidationError(ValidationCode.INVALID_EMAIL_FORMAT);
        } else {
            this.addValidationError(
                ValidationCode.INVALID_EMAIL_FORMAT,
                this.TR.key(
                    'client-admin.users.invite-users.invalid-email-message'
                )
            );
        }

        return valid;
    }

    /**
     * Validates emails to ensure the domain matches a valid domain on the organization.
     *
     * @returns boolean value whether emails are valid or invalid.
     */
    private validateAllowedEmailDomains(): boolean {
        let valid = true;
        for (const emailEntity of this.emails) {
            const domain = EmailDomainValidatorDirective.extractDomain(
                emailEntity.email
            );
            if (!domain || !this.validDomains.includes(domain.toLowerCase())) {
                emailEntity.valid = false;
                valid = false;
            }
        }

        if (valid) {
            this.removeValidationError(ValidationCode.INVALID_DOMAIN);
        } else {
            this.addValidationError(
                ValidationCode.INVALID_DOMAIN,
                this.TR.key(
                    'client-admin.users.invite-users.invalid-domain-message'
                )
            );
        }

        return valid;
    }

    /**
     * Validates emails to ensure the user does not already exist in the organization.
     *
     * @returns boolean value whether emails are valid or invalid.
     */
    private validateDuplicateUser(): boolean {
        let valid = true;

        for (const emailEntity of this.emails) {
            if (
                this.existingUserEmails.some(
                    (entity) =>
                        entity.email.toLowerCase() ===
                        emailEntity.email.toLowerCase()
                )
            ) {
                emailEntity.valid = false;
                valid = false;
            }
        }

        if (valid) {
            this.removeValidationError(ValidationCode.EMAIL_ALREADY_EXISTS);
        } else {
            this.addValidationError(
                ValidationCode.EMAIL_ALREADY_EXISTS,
                this.TR.key(
                    'client-admin.users.invite-users.org-user-known-message'
                )
            );
        }

        return valid;
    }

    /**
     * Validates emails to ensure the email hasn't already been added to the list.
     *
     * @returns boolean value whether emails are valid or invalid.
     */
    private validateDuplicateEmailEntered(): boolean {
        const emailIndices: Record<string, number[]> = {};
        for (const [index, { email }] of this.emails.entries()) {
            if (email.toLowerCase() in emailIndices) {
                emailIndices[email.toLowerCase()].push(index);
            } else {
                emailIndices[email.toLowerCase()] = [index];
            }
        }

        const entitiesChanged =
            Object.values(emailIndices)
                .filter((i) => i.length > 1)
                .reduce((acc, cur) => acc.concat(cur), [])
                .map((i) => (this.emails[i].valid = false)).length !== 0;

        if (!entitiesChanged) {
            this.removeValidationError(ValidationCode.DUPLICATE_ENTRY);
        } else {
            this.addValidationError(
                ValidationCode.DUPLICATE_ENTRY,
                this.TR.key(
                    'client-admin.users.invite-users.duplicate-email-message'
                )
            );
        }

        return !entitiesChanged;
    }

    /**
     * Add validation error to validation error array.
     *
     * @param validationCode enum used to determine validation error.
     * @param message string message to display to the end user.
     */
    private addValidationError(
        validationCode: ValidationCode,
        message: string
    ): void {
        this.validationErrors = this.validationErrors
            .filter((v) => v.validationCode !== validationCode)
            .concat([{ validationCode, message }]);
    }

    /**
     * Remove validation error from validation error array.
     *
     * @param validationCode enum used to determine validation error.
     */
    private removeValidationError(validationCode: ValidationCode): void {
        this.validationErrors = this.validationErrors.filter(
            (v) => v.validationCode !== validationCode
        );
    }

    /**
     * Resets validation on chips and messages.
     */
    private resetValidation(): void {
        this.emails.forEach((e) => (e.valid = true));
        this.validationErrors = [];
    }

    private inputReset() {
        this.form.controls.emails.setValue(null);
    }

    private autoCompleteFilter(value: string): Observable<UserStubWithState[]> {
        const filterValue = value ? value.toLowerCase() : '';
        if (filterValue.length <= 2) {
            return of([]);
        }

        // this is where you do simple contains search with filterValue on current active list
        // then add result here
        const existingEmails = this.emails
            .filter(
                (users) =>
                    users.firstName &&
                    (users.firstName.includes(filterValue) ||
                        users.lastName.includes(filterValue) ||
                        users.email.includes(filterValue))
            )
            .map((x) => encodeURIComponent(x.email));

        // else search via UserAPI
        return defer(() =>
            from(this.userApiSearchForUsers(filterValue, existingEmails))
        );
    }

    private userApiSearchForUsers(
        filterText: string,
        omitEmails: string[]
    ): Promise<UserStubWithState[]> {
        const sort = [
            new SortInfo(SortType.Name, SortDirection.Ascending),
            new SortInfo(SortType.Email, SortDirection.Ascending)
        ];
        const filters = [
            new FilterInfo({
                type: FilterType.Search,
                parameters: { term: filterText }
            }),
            ...(omitEmails && omitEmails.length > 0
                ? [
                      new FilterInfo({
                          type: FilterType.NotInEmail,
                          parameters: omitEmails
                      })
                  ]
                : [])
        ];
        return this.userApi
            .getUsers(5, filters, 'all', sort)
            .then((users) =>
                users.map((u) => this.userFactory.new_UserToStubWithState(u))
            );
    }
}
